import React, { useState } from "react";
import {
  Box,
  TextInput,
  Text,
  Button,
  Group,
  Select,
  Image,
  InputWrapper,
  FileButton,
  ActionIcon,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Mention,
  Paragraph,
  Undo,
  Font,
  Heading,
  List,
  Alignment,
  Link,
  PasteFromOffice,
  ImageInsertViaUrl,
  Image as CKImage,
  ImageInsert,
  Table,
  TableToolbar,
  Base64UploadAdapter,
} from "ckeditor5";
import {
  createPaymentChannel,
  updatePaymentChannel,
} from "../../../services/payment-channels";
import {
  loadingNotification,
  completedLoadingNotification,
} from "../../ui/Notifications";
import { IconTrash } from "@tabler/icons-react";

const FormPaymentChannels = ({ dataDetail, onCloseModal, reloadList }) => {
  const [loadingForm, setLoadingForm] = useState(false);
  const [imagePreview, setImagePreview] = useState(
    dataDetail?.logo_url || null,
  );

  // Check if gateway is Xendit, basically anything with 'API' as requested "API XENDIT"
  const isEditing = !!dataDetail;
  const [paymentType, setPaymentType] = useState(
    isEditing
      ? dataDetail.gateway?.value?.toLowerCase().includes("xendit")
        ? "api_xendit"
        : "manual"
      : "api_xendit",
  );

  const form = useForm({
    initialValues: {
      name: dataDetail?.name || "",
      gateway: dataDetail?.gateway?.value || "API XENDIT",
      instruction: dataDetail?.instruction || "",
      logo_url: null, // File object for new uploads
    },
    validate: {
      name: (val) => (!val ? "Name is required" : null),
    },
  });

  const editorConfig = {
    plugins: [
      Bold,
      Essentials,
      Italic,
      Mention,
      Paragraph,
      Undo,
      Font,
      Heading,
      List,
      Alignment,
      Link,
      PasteFromOffice,
      ImageInsertViaUrl,
      CKImage,
      ImageInsert,
      Table,
      TableToolbar,
      Base64UploadAdapter,
    ],
    alignment: {
      options: ["left", "right"],
    },
    fontSize: {
      options: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22],
      supportAllValues: true,
    },
    toolbar: {
      items: [
        "undo",
        "redo",
        "|",
        "heading",
        "|",
        "fontSize",
        "Bold",
        "Italic",
        "alignment",
        "|",
        "link",
        "|",
        "bulletedList",
        "numberedList",
        "|",
        "insertImage",
        "|",
        "insertTable",
      ],
      shouldNotGroupWhenFull: true,
    },
  };

  const handleFileChange = (file) => {
    if (file) {
      form.setFieldValue("logo_url", file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    form.setFieldValue("logo_url", null);
    setImagePreview(null);
  };

  const handleSubmit = async (values) => {
    setLoadingForm(true);
    const toastId = loadingNotification(
      "submit-payment",
      "Saving Payment Channel",
      "Please wait...",
    );

    try {
      let payload;

      if (paymentType === "manual") {
        // If it's manual, we probably want to send form data to support image upload
        payload = new FormData();
        payload.append("name", values.name);
        payload.append("gateway", "manual");
        payload.append("instruction", values.instruction);

        if (values.logo_url) {
          payload.append("logo_literal", values.logo_url);
        }
      } else {
        // API XENDIT
        payload = {
          name: values.name,
          gateway: "xendit",
        };
      }

      if (isEditing) {
        await updatePaymentChannel(dataDetail.id, payload);
      } else {
        await createPaymentChannel(payload);
      }

      completedLoadingNotification(
        toastId,
        "Success",
        `Payment Channel successfully ${isEditing ? "updated" : "created"}`,
      );
      reloadList();
      onCloseModal();
    } catch (error) {
      console.log(error);
      const msg = error.response?.data?.message || "Something went wrong";
      completedLoadingNotification(toastId, "Error", msg, "error");
    } finally {
      setLoadingForm(false);
    }
  };

  return (
    <Box>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Box mb="md">
          <Select
            label="Payment Type"
            data={[
              { value: "api_xendit", label: "API XENDIT" },
              { value: "manual", label: "Manual" },
            ]}
            value={paymentType}
            allowDeselect={false}
            onChange={(val) => {
              setPaymentType(val);
              form.setFieldValue(
                "gateway",
                val === "api_xendit" || val.toLowerCase() === "xendit"
                  ? "API XENDIT (PT Matahari Labs)"
                  : "Manual",
              );
            }}
            size="xs"
            mb="md"
            disabled={isEditing && dataDetail?.gateway?.value?.toLowerCase().includes("xendit")}
          />

          {paymentType === "api_xendit" ? (
            <Select
              label="Channel Name"
              placeholder="Select Xendit VA"
              data={["BRI", "Mandiri", "BNI", "Permata", "Credit Card", "OVO"]}
              withAsterisk
              size="xs"
              mb="md"
              {...form.getInputProps("name")}
            />
          ) : (
            <TextInput
              label="Channel Name"
              placeholder="e.g. VA BRI"
              withAsterisk
              size="xs"
              mb="md"
              {...form.getInputProps("name")}
            />
          )}

          {paymentType === "manual" && (
            <>
              <Box mb="md">
                <Text size="xs" fw={500} mb={4}>
                  Logo Image
                </Text>
                {imagePreview ? (
                  <Box style={{ position: "relative", width: "fit-content" }}>
                    <Image
                      src={imagePreview}
                      h={100}
                      w="auto"
                      fit="contain"
                      bg="gray.1"
                      radius="md"
                    />
                    <ActionIcon
                      color="red"
                      variant="filled"
                      size="sm"
                      radius="xl"
                      style={{ position: "absolute", top: -5, right: -5 }}
                      onClick={handleRemoveImage}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Box>
                ) : (
                  <FileButton
                    onChange={handleFileChange}
                    accept="image/png,image/jpeg"
                  >
                    {(props) => (
                      <Button {...props} variant="outline" size="xs">
                        Upload Logo
                      </Button>
                    )}
                  </FileButton>
                )}
              </Box>

              <InputWrapper label="Instructions" my={10} mb="md">
                <CKEditor
                  editor={ClassicEditor}
                  config={editorConfig}
                  data={form.values.instruction}
                  onChange={(event, editor) => {
                    form.setFieldValue("instruction", editor.getData());
                  }}
                />
              </InputWrapper>
            </>
          )}
        </Box>
        <Group justify="flex-end" mt="xl">
          <Button variant="outline" size="xs" onClick={onCloseModal}>
            Cancel
          </Button>
          <Button type="submit" size="xs" loading={loadingForm}>
            Submit
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default FormPaymentChannels;
